import { useState, useCallback, useEffect } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { UploadFileHelper } from '@/Domain/helper/UploadFile.helper';

export const DEFAULT_ACCEPTED_FILE_TYPES = [
  // Documents
  '.pdf',
  '.docx',
  '.doc',
  '.odt',
  '.rtf',
  // Spreadsheets
  '.xlsx',
  '.xls',
  '.ods',
  '.csv',
  '.tsv',
  // Presentations
  '.pptx',
  '.ppt',
  // eBooks
  '.epub',
  '.fb2',
  // Text & Markup
  '.txt',
  '.md',
  '.markdown',
  '.rst',
  '.org',
  // Web
  '.html',
  '.htm',
  '.xml',
  '.json',
  '.yaml',
  '.yml',
  '.toml',
  // Email
  '.eml',
  '.msg',
  // Scientific & Academic
  '.tex',
  '.latex',
  '.ipynb',
  '.bib',
  // Images (OCR)
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.bmp',
  '.tiff',
  '.tif',
  '.svg',
];

export const MAX_TEXT_FILE_SIZE_MB = 1;
// Mirrors the textual MIME types capped in appquickchat/api/parser/helpers.py.
export const TEXT_FILE_TYPES = [
  '.txt',
  '.md',
  '.markdown',
  '.rst',
  '.org',
  '.html',
  '.htm',
  '.xml',
  '.json',
  '.yaml',
  '.yml',
  '.toml',
  '.tex',
  '.latex',
  '.ipynb',
  '.bib',
  '.csv',
  '.tsv',
  '.fb2',
  '.rtf',
  '.eml',
  '.msg',
];

export const fileExtension = (fileName: string): string =>
  fileName.includes('.') ? `.${fileName.split('.').pop()?.toLowerCase()}` : '';

export const isTextFile = (fileName: string): boolean =>
  TEXT_FILE_TYPES.includes(fileExtension(fileName));

type FileWithError = File & {
  error?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  maxFileSize?: number;
  maxTextFileSize?: number;
  acceptedFileTypes?: string[];
  onUpload: (files: File[]) => void;
};

export const useUploadFile = ({
  open,
  onOpenChange,
  maxFileSize = 32,
  maxTextFileSize = MAX_TEXT_FILE_SIZE_MB,
  acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
  onUpload,
}: Props) => {
  const { t } = useTranslation(['knowledge_base']);
  const [files, setFiles] = useState<FileWithError[]>([]);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (!open) {
      setFiles([]);
    }
  }, [open]);

  const maxFileSizeBytes = maxFileSize * 1024 * 1024;
  const maxTextFileSizeBytes = maxTextFileSize * 1024 * 1024;

  const validateFiles = (acceptedFiles: File[]) =>
    acceptedFiles.map((file) => {
      const fileWithError = file as FileWithError;

      if (isTextFile(file.name) && file.size > maxTextFileSizeBytes) {
        fileWithError.error = t('modals.upload_file.text_too_large', {
          maxFileSize: maxTextFileSize,
        });
      } else if (file.size > maxFileSizeBytes) {
        fileWithError.error = t('modals.upload_file.too_large', { maxFileSize });
      }

      return fileWithError;
    });

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const validatedFiles = validateFiles(acceptedFiles);
      setFiles((prev) => [...prev, ...validatedFiles]);
    },
    [maxFileSizeBytes, maxTextFileSizeBytes, t],
  );

  const onDropRejected = useCallback(
    (fileRejections: FileRejection[]) => {
      const rejectedFiles = fileRejections.map((rejection) => {
        const fileWithError = rejection.file as FileWithError;

        if (rejection.file.size > maxFileSizeBytes) {
          fileWithError.error = isTextFile(rejection.file.name)
            ? t('modals.upload_file.text_too_large', { maxFileSize: maxTextFileSize })
            : t('modals.upload_file.too_large', { maxFileSize });
        } else {
          fileWithError.error = t('modals.upload_file.invalid_type', {
            acceptedFileTypes: acceptedFileTypes.join(', '),
          });
        }

        return fileWithError;
      });

      setFiles((prev) => [...prev, ...rejectedFiles]);
    },
    [maxFileSizeBytes, maxTextFileSizeBytes, acceptedFileTypes, t],
  );

  const getMimeType = (extension: string): string | null => {
    const mimeMap: Record<string, string> = {
      // Documents
      '.pdf': 'application/pdf',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.doc': 'application/msword',
      '.odt': 'application/vnd.oasis.opendocument.text',
      '.rtf': 'application/rtf',
      // Spreadsheets
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.xls': 'application/vnd.ms-excel',
      '.ods': 'application/vnd.oasis.opendocument.spreadsheet',
      '.csv': 'text/csv',
      '.tsv': 'text/tab-separated-values',
      // Presentations
      '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      '.ppt': 'application/vnd.ms-powerpoint',
      // eBooks
      '.epub': 'application/epub+zip',
      '.fb2': 'application/x-fictionbook+xml',
      // Text & Markup
      '.txt': 'text/plain',
      '.md': 'text/markdown',
      '.markdown': 'text/markdown',
      '.rst': 'text/x-rst',
      '.org': 'text/x-org',
      // Web
      '.html': 'text/html',
      '.htm': 'text/html',
      '.xml': 'application/xml',
      '.json': 'application/json',
      '.yaml': 'application/x-yaml',
      '.yml': 'application/x-yaml',
      '.toml': 'application/toml',
      // Email
      '.eml': 'message/rfc822',
      '.msg': 'application/vnd.ms-outlook',
      // Scientific & Academic
      '.tex': 'application/x-tex',
      '.latex': 'application/x-latex',
      '.ipynb': 'application/x-ipynb+json',
      '.bib': 'application/x-bibtex',
      // Images (OCR)
      '.png': 'image/png',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.gif': 'image/gif',
      '.webp': 'image/webp',
      '.bmp': 'image/bmp',
      '.tiff': 'image/tiff',
      '.tif': 'image/tiff',
      '.svg': 'image/svg+xml',
    };

    return mimeMap[extension] || null;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: acceptedFileTypes
      ? acceptedFileTypes.reduce((acc: Record<string, string[]>, type) => {
          if (type.startsWith('.')) {
            const mimeType = getMimeType(type);
            if (mimeType) {
              acc[mimeType] = [...(acc[mimeType] || []), type];
            }
          } else {
            acc[type] = [];
          }
          return acc;
        }, {})
      : undefined,
    multiple: true,
    maxSize: maxFileSizeBytes,
    noClick: false,
    preventDropOnDocument: true,
    useFsAccessApi: false,
    validator: () => null,
  });

  const handleUpload = async () => {
    if (files.length > 0) {
      try {
        setIsPending(true);
        const validFiles = files.filter((file) => !file.error);

        if (validFiles.length > 0 && onUpload) {
          onUpload(validFiles);
        } else if (validFiles.length === 0) {
          onOpenChange(false);
        }
      } catch (err) {
        console.error('Upload error:', err);
      } finally {
        setIsPending(false);
      }
    } else {
      onOpenChange(false);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return {
    files,
    maxFileSize,
    getRootProps,
    getInputProps,
    isDragActive,
    handleUpload,
    removeFile,
    getFileIcon: UploadFileHelper.getFileIcon,
    getFileSizeText: UploadFileHelper.getFileSizeText,
    isPending,
  };
};
